import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../Services/APIService';
import { RootState } from 'app/store';

// Interfaces
export interface Record {
  id?: string;
  personEmail: string;
  personName: string;
  category: string;
  transactionType: 'income' | 'expense';
  projAmt: number;
  actAmt: number;
  variance: number;
  group_id: string;
  service_id: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  isDeleted?: boolean;
  summary?: boolean;
  status?: 'approved' | 'pending_approval' | 'pending_doc_verification' | 'rejected' | string;
  approvedBy?: string;
  approvedAt?: string;
  doc_url?: string;
  doc_verified?: boolean;
}

export interface FormData {
  personEmail: string;
  personName: string;
  category: string;
  transactionType: 'income' | 'expense';
  projAmt: string;
  actAmt: string;
}

export interface ListRecordsPayload {
  group_id: string;
  service_id: string;
  transaction_type: 'income' | 'expense' | 'all' | string;
  current_year: string;
  time_filter:
    | 'quarter_one'
    | 'quarter_two'
    | 'quarter_three'
    | 'quarter_four'
    | 'current_month'
    | 'current_fortnight'
    | null;
  page_size: number;
  continuation_token?: string | null;
}

export interface ListRecordsResponse {
  records: Record[];
  total_count: number;
  page_size: number;
  next_token: string | null;
  summary: {
    total_records: number;
    income_count: number;
    expense_count: number;
    total_projected_income: number;
    total_actual_income: number;
    total_projected_expense: number;
    total_actual_expense: number;
    group_capacity: number;
    my_capacity: number;
  };
}

export interface SaveRecordPayload {
  group_id: string;
  service_id: string;
  record_id?: string;
  current_year: string;
  current_date: string;
  record_data: {
    id?: string;
    personEmail: string;
    personName: string;
    category: string;
    transactionType: 'income' | 'expense';
    projAmt: number;
    actAmt: number;
    status?: 'approved' | 'pending_approval' | 'pending_doc_verification' | 'rejected' | string;
    doc_url?: string;
    doc_verified?: boolean;
    doc_file?: {
      fileName: string;
      contentType: string;
      fileBase64: string;
    };
  };
}

export interface ImportRecordsPayload {
  group_id: string;
  service_id: string;
  records: { [key: string]: string | number | undefined }[];
}

export interface FetchRecordPayload {
  group_id: string;
  service_id: string;
  record_id: string;
}

export interface DeleteRecordPayload {
  group_id: string;
  service_id: string;
  record_id: string;
}

// Updated IncomeExpensesState to include previousGroupId
export interface IncomeExpensesState {
  records: Record[];
  summary: {
    total_records: number;
    income_count: number;
    expense_count: number;
    total_projected_income: number;
    total_actual_income: number;
    total_projected_expense: number;
    total_actual_expense: number;
    group_capacity: number;
    my_capacity: number;
  } | null;
  filter: 'all' | 'income' | 'expense';
  current_year: string;
  time_filter:
    | 'quarter_one'
    | 'quarter_two'
    | 'quarter_three'
    | 'quarter_four'
    | 'current_month'
    | 'current_fortnight'
    | null;
  loading: boolean;
  error: string | null;
  success: boolean;
  message: string | null;
  nextToken: string | null;
  hasMoreRecords: boolean;
  selectedRecord: Record | null;
  previousGroupId: string | null;
}

// Initial State
const initialState: IncomeExpensesState = {
  records: [],
  summary: null,
  filter: 'all',
  current_year: new Date().getFullYear().toString(),
  time_filter: 'current_month',
  loading: false,
  error: null,
  success: false,
  message: null,
  nextToken: null,
  hasMoreRecords: true,
  selectedRecord: null,
  previousGroupId: null
};

// Utility function for variance calculation
const calculateVariance = (record: Record): number => {
  return record.transactionType === 'income'
    ? record.projAmt - record.actAmt
    : record.actAmt - record.projAmt;
};

// Thunks
export const fetchRecords = createAsyncThunk<
  ListRecordsResponse,
  ListRecordsPayload,
  { state: RootState }
>('incomeExpenses/fetchRecords', async (payload, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const selectedProjectId = state.homeTilesServices?.selectedProjectId || '';
    const selectedServiceTitle = state.serviceSelected?.selectedServiceTitle || '';

    const requestPayload: any = {
      group_id: selectedProjectId,
      service_id: 'Budgeting Tools',
      transaction_type: payload.transaction_type,
      current_year: payload.current_year,
      page_size: payload.page_size,
      continuation_token: payload.continuation_token
    };

    if (payload.time_filter !== null) {
      requestPayload.time_filter = payload.time_filter;
    }

    const response = await axiosInstance.post('/income-expense/list', requestPayload);

    const records = response.data.records.map((record: any) => ({
      ...record,
      transactionType: record.transactionType.toLowerCase(),
      variance: calculateVariance(record),
      group_id: selectedProjectId,
      service_id: selectedServiceTitle
    }));

    return {
      records,
      total_count: response.data.total_count,
      page_size: response.data.page_size,
      next_token: response.data.next_token,
      summary: response.data.summary
    };
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch records');
  }
});

export const saveRecord = createAsyncThunk<Record, SaveRecordPayload, { state: RootState }>(
  'incomeExpenses/saveRecord',
  async (payload, { getState, rejectWithValue }) => {
    try {
      const requestPayload = {
        ...payload,
        record_data: {
          ...payload.record_data,
          id: payload.record_data.id || payload.record_id
        }
      };
      const response = await axiosInstance.post('/income-expense/add', requestPayload);

      const record = response.data.record;
      
      // Preserve the status from the request if not in response
      const status = record.status || payload.record_data.status || 'approved';
      
      const finalRecord = {
        ...record,
        id: record.id || payload.record_data.id,
        transactionType: record.transactionType.toLowerCase(),
        variance: calculateVariance(record),
        group_id: payload.group_id,
        service_id: payload.service_id,
        status: status, // Ensure status is always set
        doc_verified: payload.record_data.doc_verified ?? record.doc_verified
      };
      
      return finalRecord;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to save record');
    }
  }
);

export const importRecords = createAsyncThunk<Record[], ImportRecordsPayload, { state: RootState }>(
  'incomeExpenses/importRecords',
  async (payload, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const selectedProjectId = state.homeTilesServices?.selectedProjectId || '';
      const selectedServiceTitle = state.serviceSelected?.selectedServiceTitle || '';

      const response = await axiosInstance.post('/income-expense/import', {
        group_id: selectedProjectId,
        service_id: selectedServiceTitle,
        records: payload.records
      });

      const records = response.data.records.map((record: any) => ({
        ...record,
        transactionType: record.transactionType.toLowerCase(),
        variance: calculateVariance(record),
        group_id: selectedProjectId,
        service_id: selectedServiceTitle
      }));

      return records;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to import records');
    }
  }
);

export const fetchRecordById = createAsyncThunk<Record, FetchRecordPayload, { state: RootState }>(
  'incomeExpenses/fetchRecordById',
  async (payload, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const selectedProjectId = state.homeTilesServices?.selectedProjectId || '';
      const selectedServiceTitle = state.serviceSelected?.selectedServiceTitle || '';

      const response = await axiosInstance.post('/income-expense/get', {
        group_id: selectedProjectId,
        service_id: selectedServiceTitle,
        record_id: payload.record_id
      });

      const record = response.data.record;
      return {
        ...record,
        transactionType: record.transactionType.toLowerCase(),
        variance: calculateVariance(record),
        group_id: selectedProjectId,
        service_id: selectedServiceTitle
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch record');
    }
  }
);

export const deleteRecord = createAsyncThunk<void, DeleteRecordPayload, { state: RootState }>(
  'incomeExpenses/deleteRecord',
  async (payload, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const selectedProjectId = state.homeTilesServices?.selectedProjectId || '';
      const selectedServiceTitle = state.serviceSelected?.selectedServiceTitle || '';

      await axiosInstance.delete('/income-expense/delete', {
        data: {
          group_id: selectedProjectId,
          service_id: selectedServiceTitle,
          record_id: payload.record_id
        }
      });
    } catch (error: any) {
      console.error('Error deleting record:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to delete record');
    }
  }
);

// Slice
const incomeExpensesSlice = createSlice({
  name: 'incomeExpenses',
  initialState,
  reducers: {
    setFilter: (state, action: { payload: 'all' | 'income' | 'expense' }) => {
      state.filter = action.payload;
    },
    setCurrentYear: (state, action: { payload: string }) => {
      state.current_year = action.payload;
    },
    setTimeFilter: (
      state,
      action: {
        payload:
          | 'quarter_one'
          | 'quarter_two'
          | 'quarter_three'
          | 'quarter_four'
          | 'current_month'
          | 'current_fortnight'
          | null;
      }
    ) => {
      state.time_filter = action.payload;
    },
    clearRecords: (state) => {
      state.records = [];
      state.summary = null;
      state.nextToken = null;
      state.hasMoreRecords = false;
      state.error = null;
    },
    clearRecordsOnGroupChange: (state, action: { payload: string }) => {
      state.records = [];
      state.summary = null;
      state.nextToken = null;
      state.hasMoreRecords = false;
      state.error = null;
      state.previousGroupId = action.payload;
    },
    resetIncomeExpensesState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.message = null;
      state.selectedRecord = null;
    },
    resetRecordsState: (state) => {
      state.records = [];
      state.summary = null;
      state.nextToken = null;
      state.hasMoreRecords = true;
    },
    clearRecordsError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Records
      .addCase(fetchRecords.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecords.fulfilled, (state, action) => {
        state.loading = false;
        const { records, next_token, summary } = action.payload;
        if (action.meta.arg.continuation_token) {
          state.records = [...state.records, ...records];
        } else {
          state.records = records;
        }
        state.summary = summary;
        state.nextToken = next_token;
        state.hasMoreRecords = next_token !== null;
      })
      .addCase(fetchRecords.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.records = [];
      })
      // Save Record
      .addCase(saveRecord.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
        state.message = null;
      })
      .addCase(saveRecord.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = action.payload.id
          ? 'Record updated successfully'
          : 'Record added successfully';
        // For updates, patch existing record in state (if present)
        if (action.meta.arg.record_data.id) {
          const existingIndex = state.records.findIndex(r => r.id === action.payload.id);
          if (existingIndex !== -1) {
            state.records[existingIndex] = {
              ...state.records[existingIndex],
              ...action.payload
            };
          }
        } else {
          // Only add new records
          state.records.push(action.payload);
        }
      })
      .addCase(saveRecord.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
      })
      // Import Records
      .addCase(importRecords.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(importRecords.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = 'Records imported successfully';
        state.records.push(...action.payload);
      })
      .addCase(importRecords.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
      })
      // Fetch Record by ID
      .addCase(fetchRecordById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecordById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedRecord = action.payload;
      })
      .addCase(fetchRecordById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete Record
      .addCase(deleteRecord.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteRecord.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = 'Record deleted successfully';
        state.records = state.records.filter((r) => r.id !== action.meta.arg.record_id);
      })
      .addCase(deleteRecord.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
      });
  }
});

export const {
  setFilter,
  setCurrentYear,
  setTimeFilter,
  clearRecords,
  clearRecordsOnGroupChange,
  resetIncomeExpensesState,
  resetRecordsState,
  clearRecordsError
} = incomeExpensesSlice.actions;
export default incomeExpensesSlice.reducer;
